import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadList, upsertList } from '../../lib/localList';
import type { BenefitAward, BenefitClaim, BenefitType, BenefitTypeCode, EligibilityTrace } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['ASP_BENEFITS_OFFICER', 'ASP_SENIOR_OFFICER', 'ASP_ADMIN'];
const SENIOR_ROLES = ['ASP_SENIOR_OFFICER', 'ASP_ADMIN'];

/**
 * S02 has no `GET /claims` or `GET /claims/:id` (only the eligibility-trace
 * sub-resource) — this page tracks claims this browser has touched (created,
 * sent to board, or decided; each entry is the verbatim response of one of
 * those real calls) so an officer can keep working a claim across steps.
 * `GET /awards` and `GET /types` are real list endpoints and used directly.
 */
export function Claims() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.some((r) => OFFICER_ROLES.includes(r)) ?? false;
  const isAdmin = persona?.roles.includes('ASP_ADMIN') ?? false;
  const cacheKey = 'benefits.claims';

  const [claims, setClaims] = useState<BenefitClaim[]>(() => loadList<BenefitClaim>(cacheKey));
  const [traces, setTraces] = useState<Record<string, EligibilityTrace>>({});
  const [showNewClaim, setShowNewClaim] = useState(false);
  const [decideTarget, setDecideTarget] = useState<BenefitClaim | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [awardFilter, setAwardFilter] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const typesQ = useApiQuery<{ items: BenefitType[] }>('/api/v1/benefits/types');
  const awardsQ = useApiQuery<{ items: BenefitAward[] }>(isOfficer ? `/api/v1/benefits/awards${awardFilter ? `?status=${awardFilter}` : ''}` : null);

  if (!isOfficer) {
    return (
      <div>
        <PageHeader title="Claims" />
        <ErrorNote message="This view requires the Benefits Officer, Senior Officer, or ASP Administrator role." />
      </div>
    );
  }

  const upsert = (c: BenefitClaim) => setClaims(upsertList(cacheKey, c));

  const toBoard = async (c: BenefitClaim) => {
    setBusyId(c.id);
    setError(null);
    try {
      const updated = await api.post<BenefitClaim>(`/api/v1/benefits/claims/${c.id}/to-board`);
      upsert({ ...c, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not refer to the Medical Board');
    } finally {
      setBusyId(null);
    }
  };

  const checkTrace = async (c: BenefitClaim) => {
    setBusyId(c.id);
    try {
      const trace = await api.get<EligibilityTrace>(`/api/v1/benefits/claims/${c.id}/eligibility-trace`);
      setTraces((t) => ({ ...t, [c.id]: trace }));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not fetch trace');
    } finally {
      setBusyId(null);
    }
  };

  const autoCease = async () => {
    setError(null);
    try {
      const r = await api.post<{ ceased: number }>('/api/v1/benefits/maintenance/auto-cease', { asOf: new Date().toISOString().slice(0, 10) });
      setError(`Auto-cease ran: ${r.ceased} award(s) ceased for reaching their duration limit.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Auto-cease failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Statutory Benefit Claims"
        subtitle="All 13 Social Security Act benefits as one config-driven rules engine"
        actions={
          <>
            {isAdmin ? (
              <button className="btn-secondary" onClick={autoCease}>
                Run auto-cease
              </button>
            ) : null}
            <button className="btn-primary" onClick={() => setShowNewClaim(true)}>
              + New claim (on behalf)
            </button>
          </>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Benefit types & rates</h2>
        <div className="card overflow-x-auto">
          {typesQ.loading ? (
            <Spinner />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Code</th>
                  <th className="table-head">Name</th>
                  <th className="table-head">Current rate</th>
                  <th className="table-head">Medical board?</th>
                </tr>
              </thead>
              <tbody>
                {(typesQ.data?.items ?? []).map((t) => (
                  <tr key={t.id}>
                    <td className="table-cell text-xs font-medium">{t.code}</td>
                    <td className="table-cell">{t.name}</td>
                    <td className="table-cell text-xs">
                      {t.rates?.[0] ? `${scr(t.rates[0].rate)} / ${t.rates[0].unit.toLowerCase()}` : <span className="text-amber-600">not configured</span>}
                    </td>
                    <td className="table-cell text-xs">{t.medicalBoardRequired ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Claims worked this session</h2>
        <div className="card overflow-x-auto">
          {claims.length === 0 ? (
            <EmptyState title="No claims yet" hint="Submit a claim on behalf of a claimant, or process one a citizen submitted (paste its reference below once you have the id)." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Reference</th>
                  <th className="table-head">NIN</th>
                  <th className="table-head">Benefit</th>
                  <th className="table-head">Status</th>
                  <th className="table-head">Eligibility</th>
                  <th className="table-head" />
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => {
                  const trace = traces[c.id];
                  return (
                    <tr key={c.id}>
                      <td className="table-cell font-medium">{c.refNumber}</td>
                      <td className="table-cell text-xs">{c.nin}</td>
                      <td className="table-cell text-xs">{c.benefitType?.name ?? c.benefitTypeId}</td>
                      <td className="table-cell">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="table-cell text-xs">
                        {trace ? (
                          <span className={trace.trace.every((t) => t.pass) ? 'text-seagreen-600' : 'text-rose-600'}>
                            {trace.trace.filter((t) => t.pass).length}/{trace.trace.length} pass
                          </span>
                        ) : (
                          <button className="text-brand-600 hover:underline" onClick={() => checkTrace(c)} disabled={busyId === c.id}>
                            check
                          </button>
                        )}
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex justify-end gap-1.5">
                          {c.status === 'EVIDENCE_CHECK' ? (
                            <>
                              {c.benefitType?.medicalBoardRequired ? (
                                <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => toBoard(c)} disabled={busyId === c.id}>
                                  To board
                                </button>
                              ) : null}
                              <button className="btn-primary !px-2 !py-1 text-xs" onClick={() => setDecideTarget(c)}>
                                Decide
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">Awards</h2>
          <select className="field-input max-w-[160px]" value={awardFilter} onChange={(e) => setAwardFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="CEASED">Ceased</option>
          </select>
        </div>
        <div className="card overflow-x-auto">
          {awardsQ.loading ? (
            <Spinner />
          ) : (awardsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No awards" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">NIN</th>
                  <th className="table-head">Benefit</th>
                  <th className="table-head">Kind</th>
                  <th className="table-head text-right">Amount</th>
                  <th className="table-head">Start</th>
                  <th className="table-head">End</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {awardsQ.data!.items.map((a) => (
                  <tr key={a.id}>
                    <td className="table-cell text-xs">{a.nin}</td>
                    <td className="table-cell text-xs">{a.benefitCode}</td>
                    <td className="table-cell text-xs">{a.payKind}</td>
                    <td className="table-cell text-right tabular-nums">{scr(a.amount)}</td>
                    <td className="table-cell text-xs text-slate-500">{formatDate(a.startDate)}</td>
                    <td className="table-cell text-xs text-slate-500">{formatDate(a.endDate)}</td>
                    <td className="table-cell">
                      <StatusBadge status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showNewClaim ? (
        <NewClaimModal
          types={typesQ.data?.items ?? []}
          onClose={() => setShowNewClaim(false)}
          onDone={(c) => {
            upsert(c);
            setShowNewClaim(false);
          }}
        />
      ) : null}
      {decideTarget ? (
        <DecideModal
          claim={decideTarget}
          isSenior={persona?.roles.some((r) => SENIOR_ROLES.includes(r)) ?? false}
          onClose={() => setDecideTarget(null)}
          onDone={(c) => {
            upsert(c);
            setDecideTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

function NewClaimModal({ types, onClose, onDone }: { types: BenefitType[]; onClose: () => void; onDone: (c: BenefitClaim) => void }) {
  const [nin, setNin] = useState('');
  const [dob, setDob] = useState('');
  const [code, setCode] = useState<BenefitTypeCode | ''>('');
  const [evidence, setEvidence] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const claim = await api.post<BenefitClaim>('/api/v1/benefits/claims', {
        nin,
        dob,
        benefitTypeCode: code,
        evidenceRefs: evidence.split(',').map((s) => s.trim()).filter(Boolean),
      });
      const type = types.find((t) => t.code === code);
      onDone(type ? { ...claim, benefitType: type } : claim);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit claim');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Submit claim on behalf of a claimant" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Claimant NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Date of birth">
          <input className="field-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </Field>
        <Field label="Benefit type">
          <select className="field-input" value={code} onChange={(e) => setCode(e.target.value as BenefitTypeCode)}>
            <option value="">Select…</option>
            {types.map((t) => (
              <option key={t.id} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Evidence references (comma-separated)">
          <input className="field-input" value={evidence} onChange={(e) => setEvidence(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !nin || !dob || !code}>
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DecideModal({
  claim,
  isSenior,
  onClose,
  onDone,
}: {
  claim: BenefitClaim;
  isSenior: boolean;
  onClose: () => void;
  onDone: (c: BenefitClaim) => void;
}) {
  const [approve, setApprove] = useState(true);
  const [note, setNote] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [overrideReason, setOverrideReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ claim: BenefitClaim; award?: BenefitAward } | BenefitClaim>(`/api/v1/benefits/claims/${claim.id}/decide`, {
        approve,
        note,
        startDate,
        overrideReason: overrideReason || undefined,
      });
      const updated = 'claim' in result ? result.claim : result;
      onDone({ ...claim, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Decision failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Decide claim ${claim.refNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="flex gap-3">
          <label className="flex items-center gap-1.5 text-sm">
            <input type="radio" checked={approve} onChange={() => setApprove(true)} /> Approve
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="radio" checked={!approve} onChange={() => setApprove(false)} /> Reject
          </label>
        </div>
        <Field label="Decision start date">
          <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Note">
          <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {approve && isSenior ? (
          <Field label="Override reason (only needed if the eligibility trace fails)">
            <input className="field-input" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
          </Field>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || note.length < 3}>
            {busy ? 'Saving…' : 'Confirm decision'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
